import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import Header from "./Header";
import { api } from "../server";

const TAGS = ["", "Popular", "Best Value", "Economy", "New"];
const TYPES = ["both", "individual", "corporate"];
const MAX_IMAGES = 6;
const MAX_FILE_MB = 5;
const ALLOWED_MIME = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];

const money = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number.isFinite(Number(n)) ? Number(n) : 0);

function Products() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState("ACTIVE");

  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const formRef = useRef(null);
  const fileInputRef = useRef(null);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [tag, setTag] = useState("");
  const [productType, setProductType] = useState("both");
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);

  // images state — array of public URLs
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({}); // { localId: percent }

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get("/api/admin/products");
      setRows(data?.products || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const resetForm = () => {
    setEditing(null);
    setName("");
    setDescription("");
    setPrice("");
    setTag("");
    setProductType("both");
    setSortOrder(0);
    setIsActive(true);
    setImages([]);
    setUploadProgress({});
  };
  const openCreate = () => {
    resetForm();
    setOpenForm(true);
    setTimeout(
      () =>
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      50,
    );
  };
  const openEdit = (r) => {
    setEditing(r);
    setName(r.name || "");
    setDescription(r.description || "");
    setPrice(String(r.price ?? ""));
    setTag(r.tag || "");
    setProductType(r.productType || "both");
    setSortOrder(Number(r.sortOrder) || 0);
    setIsActive(!!r.isActive);
    // hydrate from images[]; fall back to legacy single image field
    const imgs =
      Array.isArray(r.images) && r.images.length > 0
        ? r.images
        : r.image
          ? [r.image]
          : [];
    setImages(imgs);
    setUploadProgress({});
    setOpenForm(true);
    setTimeout(
      () =>
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      50,
    );
  };
  const closeForm = () => {
    setOpenForm(false);
    resetForm();
  };

  /**
   * Upload selected files via presigned PUT URLs.
   * 1. Ask backend for a presign batch
   * 2. PUT each file directly to S3 with progress
   * 3. Append the public URLs to images[]
   */
  const onPickFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    // reset input so the same file can be picked again later
    if (fileInputRef.current) fileInputRef.current.value = "";

    if (!picked.length) return;

    // Validate
    const remainingSlots = MAX_IMAGES - images.length;
    if (remainingSlots <= 0) {
      alert(`Maximum ${MAX_IMAGES} images per product.`);
      return;
    }
    const files = picked.slice(0, remainingSlots);
    if (picked.length > remainingSlots) {
      alert(`Only ${remainingSlots} more image(s) allowed; extras ignored.`);
    }

    for (const f of files) {
      if (!ALLOWED_MIME.includes(f.type)) {
        alert(`${f.name}: unsupported type (${f.type}). Use JPG/PNG/WEBP/GIF.`);
        return;
      }
      if (f.size > MAX_FILE_MB * 1024 * 1024) {
        alert(`${f.name}: too large (max ${MAX_FILE_MB} MB).`);
        return;
      }
    }

    setUploading(true);
    try {
      // 1. Ask backend for presigned URLs
      const presignBody = {
        folder: "products",
        files: files.map((f) => ({
          contentType: f.type,
          originalName: f.name,
        })),
      };
      const data = await api.post("/api/admin/uploads/presign", presignBody);
      const uploads = data?.uploads || [];
      if (uploads.length !== files.length) {
        throw new Error("Presign mismatch");
      }

      // 2. PUT each file in parallel, with progress
      const uploadedUrls = await Promise.all(
        files.map((file, idx) => putWithProgress(uploads[idx], file)),
      );

      // 3. Append URLs to images array
      setImages((prev) => [...prev, ...uploadedUrls]);
    } catch (err) {
      console.error(err);
      alert(err.message || "Upload failed");
    } finally {
      setUploading(false);
      setUploadProgress({});
    }
  };

  /**
   * Upload one file to a presigned URL using XHR (gives progress).
   * Returns the public URL on success.
   */
  const putWithProgress = (presigned, file) => {
    const localId = `${file.name}-${file.size}-${Date.now()}`;
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", presigned.uploadUrl, true);
      xhr.setRequestHeader("Content-Type", presigned.contentType);
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable) {
          const pct = Math.round((ev.loaded / ev.total) * 100);
          setUploadProgress((prev) => ({ ...prev, [localId]: pct }));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setUploadProgress((prev) => {
            const cp = { ...prev };
            delete cp[localId];
            return cp;
          });
          resolve(presigned.publicUrl);
        } else {
          reject(new Error(`S3 upload failed (${xhr.status})`));
        }
      };
      xhr.onerror = () => reject(new Error("Network error during upload"));
      xhr.send(file);
    });
  };

  const removeImage = (idx) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };
  const moveImage = (idx, dir) => {
    setImages((prev) => {
      const next = [...prev];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= next.length) return prev;
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next;
    });
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    if (!name.trim()) return alert("Name is required.");
    if (price === "" || isNaN(Number(price)))
      return alert("Valid price is required.");
    if (uploading) return alert("Wait for image uploads to complete.");

    const payload = {
      name: name.trim(),
      description: description.trim(),
      price: Number(price),
      tag,
      productType,
      sortOrder: Number(sortOrder) || 0,
      isActive,
      images,
    };

    setSaving(true);
    try {
      if (editing) {
        const data = await api.put(
          `/api/admin/products/${editing._id}`,
          payload,
        );
        const updated = data?.product;
        setRows((prev) =>
          prev.map((x) => (x._id === editing._id ? { ...x, ...updated } : x)),
        );
      } else {
        const data = await api.post("/api/admin/products", payload);
        const created = data?.product;
        if (created) setRows((prev) => [created, ...prev]);
      }
      closeForm();
    } catch (err) {
      alert(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (id) => {
    const r = rows.find((x) => x._id === id);
    if (!r) return;
    try {
      const data = await api.put(`/api/admin/products/${id}`, {
        isActive: !r.isActive,
      });
      const updated = data?.product;
      setRows((prev) =>
        prev.map((x) => (x._id === id ? { ...x, ...updated } : x)),
      );
    } catch (err) {
      alert(err.message || "Update failed");
    }
  };
  const removeProduct = async (id) => {
    if (!window.confirm("Deactivate this product?")) return;
    try {
      await api.delete(`/api/admin/products/${id}`);
      setRows((prev) =>
        prev.map((x) => (x._id === id ? { ...x, isActive: false } : x)),
      );
    } catch (err) {
      alert(err.message || "Delete failed");
    }
  };

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return rows
      .filter((r) => {
        const matchQ =
          !query ||
          String(r.name || "")
            .toLowerCase()
            .includes(query) ||
          String(r.description || "")
            .toLowerCase()
            .includes(query);
        const matchActive =
          active === "ALL"
            ? true
            : active === "ACTIVE"
              ? !!r.isActive
              : !r.isActive;
        return matchQ && matchActive;
      })
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  }, [rows, q, active]);

  const stats = useMemo(() => {
    const total = rows.length;
    const act = rows.filter((x) => x.isActive).length;
    const corp = rows.filter((x) => x.productType === "corporate").length;
    return { total, active: act, inactive: total - act, corporate: corp };
  }, [rows]);

  // first image for thumbnail; falls back to legacy fields
  const firstImage = (r) => {
    if (Array.isArray(r.images) && r.images.length > 0) return r.images[0];
    if (r.image) return r.image;
    return null;
  };

  return (
    <>
      <Header />
      <div id="db-wrapper">
        <main id="page-content">
          <section className="container-fluid p-4 pt-3">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <div>
                <h3 className="mb-0 lh-1 ls-sm fw-normal">Products</h3>
                <div className="text-secondary lh-1">
                  Water bottles • Cans • Bulk options
                </div>
              </div>
              <div className="d-flex gap-1">
                <button
                  type="button"
                  className="btn btn-outline-secondary border-light px-4 text-dark bg-white"
                  onClick={() => {
                    setQ("");
                    setActive("ACTIVE");
                  }}
                >
                  <i className="bi bi-funnel me-2" />
                  Reset Filters
                </button>
                <button className="btn btn-primary px-4" onClick={openCreate}>
                  <i className="bi bi-plus-circle me-2" />
                  Add Product
                </button>
              </div>
            </div>

            <div className="row g-2 mb-2">
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-droplet" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Total</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.total}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-success-subtle text-success"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-check2-circle" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Active</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.active}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-info-subtle text-info"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-building" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">
                        Corporate Only
                      </div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.corporate}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-danger-subtle text-danger"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-pause-circle" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Inactive</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.inactive}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body p-3 p-md-4">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div
                    className="rounded-3 d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary"
                    style={{ width: 40, height: 40 }}
                  >
                    <i className="bi bi-search" />
                  </div>
                  <div>
                    <div className="fw-semibold lh-1">Search & Filters</div>
                    <div className="text-secondary small">
                      Find a product by name or description
                    </div>
                  </div>
                </div>
                <div className="row g-2">
                  <div className="col-12 col-md-9">
                    <label className="form-label mb-1 fw-normal">Search</label>
                    <input
                      className="form-control"
                      placeholder="Product name or description"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                    />
                  </div>
                  <div className="col-12 col-md-3">
                    <label className="form-label mb-1 fw-normal">Status</label>
                    <select
                      className="form-select"
                      value={active}
                      onChange={(e) => setActive(e.target.value)}
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                      <option value="ALL">All</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {openForm && (
              <div
                ref={formRef}
                className="card border-0 shadow-sm rounded-4 mt-2"
              >
                <div className="card-body p-3 p-md-4">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="d-flex align-items-center gap-2">
                      <div
                        className="rounded-3 d-inline-flex align-items-center justify-content-center bg-success-subtle text-success"
                        style={{ width: 40, height: 40 }}
                      >
                        <i
                          className={`bi ${editing ? "bi-pencil-square" : "bi-droplet-half"}`}
                        />
                      </div>
                      <div>
                        <div className="fw-semibold lh-1">
                          {editing ? "Edit Product" : "Add Product"}
                        </div>
                        <div className="text-secondary small">
                          {editing
                            ? "Update product details, images, and pricing"
                            : "Add a new product to your catalog"}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-secondary border-light px-4 text-dark bg-white"
                      onClick={closeForm}
                    >
                      <i className="bi bi-x-lg me-1" />
                      Close
                    </button>
                  </div>

                  <form onSubmit={saveProduct}>
                    <div className="row g-2">
                      {/* IMAGES — full width */}
                      <div className="col-12">
                        <label className="form-label mb-1 fw-normal">
                          Product Images{" "}
                          <span className="text-secondary small">
                            (max {MAX_IMAGES}, up to {MAX_FILE_MB} MB each — JPG / PNG / WEBP)
                          </span>
                        </label>

                        <div className="d-flex flex-wrap gap-2 align-items-start">
                          {/* Existing images */}
                          {images.map((url, idx) => (
                            <div
                              key={url + idx}
                              className="position-relative border rounded-3 overflow-hidden bg-light"
                              style={{ width: 110, height: 110 }}
                            >
                              <img
                                src={url}
                                alt={`product-${idx}`}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                              {idx === 0 && (
                                <span
                                  className="position-absolute top-0 start-0 m-1 badge bg-primary"
                                  style={{ fontSize: 10 }}
                                >
                                  Primary
                                </span>
                              )}
                              <button
                                type="button"
                                title="Remove"
                                onClick={() => removeImage(idx)}
                                className="btn btn-sm btn-light border position-absolute top-0 end-0 m-1 p-1"
                                style={{ lineHeight: 0 }}
                              >
                                <i className="bi bi-x" />
                              </button>
                              <div className="position-absolute bottom-0 start-0 end-0 d-flex justify-content-between p-1 bg-white bg-opacity-75">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-light border p-1"
                                  style={{ lineHeight: 0 }}
                                  onClick={() => moveImage(idx, -1)}
                                  disabled={idx === 0}
                                  title="Move left"
                                >
                                  <i className="bi bi-chevron-left" />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-light border p-1"
                                  style={{ lineHeight: 0 }}
                                  onClick={() => moveImage(idx, +1)}
                                  disabled={idx === images.length - 1}
                                  title="Move right"
                                >
                                  <i className="bi bi-chevron-right" />
                                </button>
                              </div>
                            </div>
                          ))}

                          {/* In-flight uploads (progress) */}
                          {Object.entries(uploadProgress).map(([id, pct]) => (
                            <div
                              key={id}
                              className="border rounded-3 d-flex flex-column align-items-center justify-content-center bg-light"
                              style={{ width: 110, height: 110 }}
                            >
                              <div
                                className="spinner-border spinner-border-sm text-primary mb-2"
                                role="status"
                              />
                              <div className="small text-secondary">
                                {pct}%
                              </div>
                            </div>
                          ))}

                          {/* Add button */}
                          {images.length < MAX_IMAGES && (
                            <button
                              type="button"
                              className="btn btn-light border rounded-3 d-flex flex-column align-items-center justify-content-center text-secondary"
                              style={{ width: 110, height: 110 }}
                              disabled={uploading}
                              onClick={() => fileInputRef.current?.click()}
                            >
                              {uploading ? (
                                <span className="spinner-border spinner-border-sm" />
                              ) : (
                                <>
                                  <i className="bi bi-cloud-arrow-up fs-3" />
                                  <span className="small mt-1">
                                    Add Image{images.length === 0 ? "s" : ""}
                                  </span>
                                </>
                              )}
                            </button>
                          )}

                          <input
                            ref={fileInputRef}
                            type="file"
                            accept={ALLOWED_MIME.join(",")}
                            multiple
                            hidden
                            onChange={onPickFiles}
                          />
                        </div>

                        {images.length === 0 && (
                          <div className="text-secondary small mt-1">
                            The first image will be shown as the primary thumbnail.
                          </div>
                        )}
                      </div>

                      <div className="col-12 col-md-9">
                        <label className="form-label mb-1 fw-normal">
                          Name *
                        </label>
                        <input
                          className="form-control"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="e.g. 20L Water Can"
                          required
                        />
                      </div>
                      <div className="col-6 col-md-3">
                        <label className="form-label mb-1 fw-normal">
                          Price (₹) *
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="form-control"
                          value={price}
                          onChange={(e) => setPrice(e.target.value)}
                          placeholder="0"
                          required
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label mb-1 fw-normal">
                          Description
                        </label>
                        <input
                          className="form-control"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder="Short product description"
                        />
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="form-label mb-1 fw-normal">Tag</label>
                        <select
                          className="form-select"
                          value={tag}
                          onChange={(e) => setTag(e.target.value)}
                        >
                          {TAGS.map((t) => (
                            <option key={t} value={t}>
                              {t || "— None —"}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="form-label mb-1 fw-normal">
                          Type
                        </label>
                        <select
                          className="form-select"
                          value={productType}
                          onChange={(e) => setProductType(e.target.value)}
                        >
                          {TYPES.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="form-label mb-1 fw-normal">
                          Sort Order
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={sortOrder}
                          onChange={(e) => setSortOrder(e.target.value)}
                          placeholder="0"
                        />
                      </div>
                      <div className="col-12">
                        <div className="form-check form-switch mt-2">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="prdActive"
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="prdActive"
                          >
                            Active
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex gap-2 mt-3 justify-content-end">
                      <button
                        type="button"
                        className="btn btn-light px-4"
                        onClick={closeForm}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary px-4"
                        disabled={saving || uploading}
                      >
                        {(saving || uploading) && (
                          <span className="spinner-border spinner-border-sm me-2" />
                        )}
                        {uploading
                          ? "Uploading..."
                          : saving
                            ? "Saving..."
                            : editing
                              ? "Update Product"
                              : "Save Product"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            <div className="card border-0 shadow-sm rounded-4 mt-2">
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" />
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="text-center py-5 text-secondary">
                    <i className="bi bi-inbox fs-1 d-block mb-2 opacity-50" />
                    No products
                  </div>
                ) : (
                  <div className="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table className="table mb-0 text-nowrap table-centered">
                      <tbody>
                        {filtered.map((r, index) => {
                          const thumb = firstImage(r);
                          const imgCount = Array.isArray(r.images)
                            ? r.images.length
                            : r.image
                              ? 1
                              : 0;
                          return (
                            <tr key={r._id}>
                              <td>{index + 1}.</td>
                              <td style={{ width: 90 }}>
                                {thumb ? (
                                  <div className="position-relative">
                                    <img
                                      src={thumb}
                                      alt={r.name}
                                      style={{
                                        width: 70,
                                        height: 70,
                                        objectFit: "cover",
                                        borderRadius: 8,
                                        border: "1px solid #eee",
                                      }}
                                    />
                                    {imgCount > 1 && (
                                      <span
                                        className="position-absolute bottom-0 end-0 badge bg-dark"
                                        style={{ fontSize: 10 }}
                                      >
                                        +{imgCount - 1}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <div
                                    className="d-flex align-items-center justify-content-center bg-light text-secondary rounded-3"
                                    style={{ width: 70, height: 70 }}
                                  >
                                    <i className="bi bi-image fs-4 opacity-50" />
                                  </div>
                                )}
                              </td>
                              <td className="lh-sm py-4 text-wrap">
                                <span className="text-dark">Product</span>{" "}
                                &mdash; {r.name}
                                <br />
                                <span className="text-dark">
                                  Description
                                </span>{" "}
                                &mdash; {r.description || "—"}
                                <br />
                                <span className="text-dark">
                                  Price
                                </span>{" "}
                                &mdash; {money(r.price)}
                                <br />
                                <span className="text-dark">
                                  Tag
                                </span>{" "}
                                &mdash;{" "}
                                {r.tag ? (
                                  <span className="badge bg-warning-subtle text-warning">
                                    {r.tag}
                                  </span>
                                ) : (
                                  "—"
                                )}
                                <br />
                                <span className="text-dark">
                                  Type
                                </span>{" "}
                                &mdash; {r.productType}
                                <br />
                                <span className="text-dark">
                                  Sort Order
                                </span>{" "}
                                &mdash; {r.sortOrder || 0}
                                <br />
                                <span className="text-dark">
                                  Status
                                </span>{" "}
                                &mdash;{" "}
                                {r.isActive ? (
                                  <span className="text-success">Active</span>
                                ) : (
                                  <span className="text-danger">Inactive</span>
                                )}
                              </td>
                              <td>
                                <div className="d-flex flex-column gap-1 align-items-start">
                                  <span
                                    onClick={() => openEdit(r)}
                                    className="badge bg-success-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                  >
                                    Update
                                  </span>
                                  <span
                                    onClick={() => toggleActive(r._id)}
                                    className={`badge cursor-pointer px-4 rounded-3 btn-py-sm ${
                                      r.isActive
                                        ? "bg-warning-soft"
                                        : "bg-success-soft"
                                    }`}
                                  >
                                    {r.isActive ? "Deactivate" : "Activate"}
                                  </span>
                                  <span
                                    onClick={() => removeProduct(r._id)}
                                    className="badge bg-danger-soft cursor-pointer px-4 rounded-3 btn-py-sm"
                                  >
                                    Delete
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  );
}

export default Products;